import React, { useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { StatCard } from '../components/StatCard';
import { Badge } from '../components/Badge';

const DEMO_EVALUATIONS = [
  {
    id: 'ev-1',
    employee: 'Martín Gómez',
    period: 'H1 2026',
    manager: 'Mauro Lucena',
    status: 'pending_manager',
    score: 4.2,
    nextActionKey: 'evaluations.nextActionManager',
  },
  {
    id: 'ev-2',
    employee: 'Diego Rojas',
    period: 'H1 2026',
    manager: 'Mauro Lucena',
    status: 'pending_employee',
    score: null,
    nextActionKey: 'evaluations.nextActionSelf',
  },
  {
    id: 'ev-3',
    employee: 'Sofía Herrera',
    period: '2025 · anual',
    manager: 'Pablo Páez',
    status: 'completed',
    score: 4.6,
    nextActionKey: 'evaluations.nextActionArchive',
  },
  {
    id: 'ev-4',
    employee: 'Equipo Marketing',
    period: 'Q1 2026',
    manager: 'Ana Martínez',
    status: 'draft',
    score: null,
    nextActionKey: 'evaluations.nextActionPublish',
  },
  {
    id: 'ev-5',
    employee: 'Valentina Ruiz',
    period: 'H2 2025',
    manager: 'Ana Martínez',
    status: 'archived',
    score: 3.9,
    nextActionKey: 'evaluations.nextActionNone',
  },
];

const STATUS_BADGE = {
  draft: 'draft',
  pending_employee: 'pending',
  pending_manager: 'submitted',
  completed: 'approved',
  archived: 'cancelled',
};

export function PerformanceReviews() {
  const { t } = useApp();

  const kpis = useMemo(() => {
    const active = DEMO_EVALUATIONS.filter((e) =>
      ['draft', 'pending_employee', 'pending_manager'].includes(e.status)
    ).length;
    const pending = DEMO_EVALUATIONS.filter((e) =>
      ['pending_employee', 'pending_manager'].includes(e.status)
    ).length;
    const completed = DEMO_EVALUATIONS.filter((e) => e.status === 'completed').length;
    const scores = DEMO_EVALUATIONS.map((e) => e.score).filter((s) => typeof s === 'number');
    const avg = scores.length ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10 : '—';
    return { active, pending, completed, avg };
  }, []);

  return (
    <div className="page-stack people-suite-page people-suite-page--eval">
      <div className="people-suite-hero">
        <p className="people-suite-hero__eyebrow">{t('evaluations.eyebrow')}</p>
        <h2 className="people-suite-hero__title">{t('evaluations.heroTitle')}</h2>
        <p className="people-suite-hero__lead">{t('evaluations.heroLead')}</p>
        <span className="banner--muted people-suite-hero__banner">{t('evaluations.demoBanner')}</span>
      </div>

      <div className="people-suite-kpis">
        <StatCard icon="◎" label={t('evaluations.kpiActive')} value={kpis.active} tone="accent" />
        <StatCard icon="◷" label={t('evaluations.kpiPending')} value={kpis.pending} tone="warning" />
        <StatCard icon="✓" label={t('evaluations.kpiCompleted')} value={kpis.completed} tone="success" />
        <StatCard icon="★" label={t('evaluations.kpiAvg')} value={kpis.avg} />
      </div>

      <div className="people-suite-two-col">
        <section className="panel panel--elevated people-suite-panel">
          <h3 className="people-suite-panel__title">{t('evaluations.cycleTitle')}</h3>
          <p className="panel__muted people-suite-panel__intro">{t('evaluations.cycleIntro')}</p>
          <ol className="people-suite-cycle">
            <li>
              <span className="people-suite-cycle__step">1</span>
              <div>
                <strong>{t('evaluations.cycleObjectives')}</strong>
                <p className="panel__muted text-sm">{t('evaluations.cycleObjectivesDesc')}</p>
              </div>
            </li>
            <li>
              <span className="people-suite-cycle__step">2</span>
              <div>
                <strong>{t('evaluations.cycleSelf')}</strong>
                <p className="panel__muted text-sm">{t('evaluations.cycleSelfDesc')}</p>
              </div>
            </li>
            <li>
              <span className="people-suite-cycle__step">3</span>
              <div>
                <strong>{t('evaluations.cycleManager')}</strong>
                <p className="panel__muted text-sm">{t('evaluations.cycleManagerDesc')}</p>
              </div>
            </li>
            <li>
              <span className="people-suite-cycle__step">4</span>
              <div>
                <strong>{t('evaluations.cycleFeedback')}</strong>
                <p className="panel__muted text-sm">{t('evaluations.cycleFeedbackDesc')}</p>
              </div>
            </li>
          </ol>
        </section>

        <section className="panel panel--elevated people-suite-panel people-suite-panel--accent">
          <h3 className="people-suite-panel__title">{t('evaluations.listTitle')}</h3>
          <ul className="people-suite-card-list">
            {DEMO_EVALUATIONS.map((row) => (
              <li key={row.id} className="people-suite-eval-card">
                <div className="people-suite-eval-card__top">
                  <div>
                    <div className="people-suite-eval-card__name">{row.employee}</div>
                    <div className="people-suite-eval-card__meta">
                      {row.period} · {t('evaluations.managerLabel')}: {row.manager}
                    </div>
                  </div>
                  <Badge variant={STATUS_BADGE[row.status] || 'mutedFallback'}>
                    {t(`evaluations.status_${row.status}`)}
                  </Badge>
                </div>
                <div className="people-suite-eval-card__bottom">
                  <span className="people-suite-eval-card__score">
                    {row.score != null ? `${t('evaluations.scoreLabel')}: ${row.score}` : t('evaluations.scorePending')}
                  </span>
                  <span className="people-suite-eval-card__next">{t(row.nextActionKey)}</span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>

    </div>
  );
}
